import { useMemo, useState } from "react";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import GridCard from "../../../components/shared/gridCard/GridCard";
import Button from "../../../components/shared/buttons/button";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import TotalCase from "../../../images/totalCase.svg";
import KgEntered from "../../../images/material_kg.svg";
import ColdStorage from "../../../images/cold_storage.svg";
import ColdStorageKg from "../../../images/material_orange.svg";
import TotalRipening from "../../../images/ripening_orange.svg";
import RoomKg from "../../../images/material_orange.svg";
import Sorting from "../../../images/sorting.svg";
import TotalEmployee from "../../../images/total_emp.svg";
import EmployeeColdStorage from "../../../images/emp_coldStorage.svg";
import EmployeeRipening from "../../../images/emp_ripening.svg";
import EmployeeSorting from "../../../images/emp_sorting.svg";
import AbsentEmployee from "../../../images/emp_absent.svg";
import {
  useGetEmployeeQuery,
  useGetRipeningSummaryQuery,
  useSearchRipeningBarChartQuery,
  useSearchSortingPieChartQuery,
} from "./ripeingDashboardApi";
import AddRipeningEmployeeData from "./AddRipeningEmployeeData";
import {
  useGetAllWorkingAreasQuery,
  useGetAttendanceOverviewQuery,
} from "../../../app/globalApi";
import { skipToken } from "@reduxjs/toolkit/query";
import GridCardNew from "../../../components/shared/gridCard/GridCardNew";
import EmployeeWashing from "../../../images/emp_washing.svg";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import DashboardSection from "../../../features/dashboard/components/DashboardSection";
import "../../../features/dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { SnowflakeIconSvg } from "../../../svgFiles/SnowflakeIconSvg";
import { RipeningIconSvg } from "../../../svgFiles/RipeningIconSvg";
import { SortingIconSvg } from "../../../svgFiles/SortingIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import { TrendingUpIconSvg } from "../../../svgFiles/TrendingUpIconSvg";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const RipeningDashboard = () => {
  const [showAddEmployment, setShowAddEmployment] = useState(false);
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate('YYYY-MM-DD');
  const [date, setDate] = useState(currentDate);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [tab, setTab] = useState("dashboard");

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);

    if (/%/.test(label)) {
      return /%/.test(strValue) ? strValue : `${strValue} %`;
    }
    if (/per\s*kg|\/\s*kg/i.test(label)) {
      return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    }
    if (/\bkg\b|kilo|weight/i.test(label)) {
      return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    }
    return strValue;
  };

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  const { data: summaryData } = useGetRipeningSummaryQuery({ tab, date });
  const { data: employeeSummaryData } = useGetEmployeeQuery({
    date: currentDate,
  });

  // Chart queries
  const { data: barChartData } = useSearchRipeningBarChartQuery({
    start_date: startDate,
    end_date: endDate,
  });
  const { data: pieSortingData } = useSearchSortingPieChartQuery({
    start_date: startDate,
    end_date: endDate,
  });

  const { data: workingAreasData, isLoading: isWorkingAreasLoading } =
    useGetAllWorkingAreasQuery();

  const receivingAreaId = useMemo(() => {
    if (!workingAreasData?.data) return null;
    const receivingArea = workingAreasData.data.find(
      (area) => area.name === "Ripening & Selection Area"
    );
    return receivingArea ? receivingArea.id : null;
  }, [workingAreasData]);

  // const {
  //   data: attendanceOverviewData,
  //   isLoading: isAttendanceLoading,
  //   isError: isAttendanceError,
  // } = useGetAttendanceOverviewQuery(
  //   receivingAreaId
  //     ? { working_area_id: receivingAreaId, date: date }
  //     : skipToken
  // );

  const listItems = [
    {
      label: _t("total_on_floor_cases", "Total on Floor Cases"),
      value: summaryData?.data.total_cases_on_floor,
      icon: TotalCase,
    },
    {
      label: _t("total_kg_on_floor", "Total KG on Floor"),
      value: summaryData?.data.total_kg_on_floor,
      icon: KgEntered,
    },
    {
      label: _t("total_cases_cold_storage", "Total Cases in Cold Storage"),
      value: summaryData?.data.total_cases_in_cold_storage,
      icon: ColdStorage,
    },
    {
      label: _t("total_kg_cold_storage", "Total KG in Cold Storage"),
      value: summaryData?.data.total_kg_in_cold_storage,
      icon: ColdStorageKg,
    },
    {
      label: _t("total_cases_ripening", "Total Cases in Ripening"),
      value: summaryData?.data.total_cases_in_ripening,
      icon: TotalRipening,
    },
    {
      label: _t("total_kg_rooms", "Total KG in Ripening"),
      value: summaryData?.data.total_kg_in_ripening,
      icon: RoomKg,
    },
    {
      label: _t("cases_enter_sorting", "Cases in Sorting"),
      value: summaryData?.data.total_cases_in_sorting,
      icon: Sorting,
    },
    {
      label: _t("total_kg_sorting", "Total KG in Sorting"),
      value: summaryData?.data.total_kg_in_sorting,
      icon: KgEntered,
    },
  ];

  // Cold Storage Overview Items
  const coldStorageListItems = [
    {
      label: _t("total_pallets", "Total Pallets"),
      value: summaryData?.data.total_pallets || "0",
      bgColor: "#fef3c7",
      textColor: "#fb923c",
    },
    {
      label: _t("total_cases_cold_storage", "Total Cases in Cold Storage"),
      value: summaryData?.data.total_cases_in_cold_storage,
      bgColor: "#f0fdf4",
      textColor: "#4ade80",
    },
    {
      label: _t("total_fruit_entered_kg", "Total Fruit Entered / KG"),
      value: summaryData?.data.total_kg_in_cold_storage,
      bgColor: "#f5e7ff",
      textColor: "#a78bfa",
    },
  ];

  // Ripening Rooms Overview Items
  const ripeningRoomsListItems = [
    {
      label: _t("total_cases_ripening", "Total Cases Entered in Rooms"),
      value: summaryData?.data.total_cases_in_ripening,
      bgColor: "#fef2f0",
      textColor: "#f87171",
    },
    {
      label: _t("total_kg_rooms", "Total Weight Entered in Rooms"),
      value: summaryData?.data.total_kg_in_ripening,
      bgColor: "#fef2f0",
      textColor: "#f87171",
    },
  ];

  // Sorting Overview Items - Expanded
  const sortingListItems = [
    {
      label: _t("cases_enter_sorting", "Cases in Sorting"),
      value: summaryData?.data.total_cases_in_sorting,
      bgColor: "#fef3c7",
      textColor: "#fb923c",
    },
    {
      label: _t("total_kg_sorting", "KG Entered in Sorting"),
      value: summaryData?.data.total_kg_in_sorting,
      bgColor: "#fef3c7",
      textColor: "#fb923c",
    },
    {
      label: _t("cases_entered_processing", "Cases Entered Processing"),
      value: summaryData?.data.cases_entered_processing || "0",
      bgColor: "#dbeafe",
      textColor: "#60a5fa",
    },
    {
      label: _t("kg_entered_processing", "KG Entered Processing"),
      value: summaryData?.data.kg_entered_processing || "0",
      bgColor: "#dbeafe",
      textColor: "#60a5fa",
    },
    {
      label: _t("cases_rejected", "Cases Rejected"),
      value: summaryData?.data.cases_rejected || "0",
      bgColor: "#fee2e2",
      textColor: "#f87171",
    },
    {
      label: _t("kg_rejected", "KG Rejected"),
      value: summaryData?.data.kg_rejected || "0",
      bgColor: "#fee2e2",
      textColor: "#f87171",
    },
    {
      label: _t("kg_evaporated", "KG Evaporated"),
      value: summaryData?.data.kg_evaporated || "0",
      bgColor: "#fecdd3",
      textColor: "#f472b6",
    },
    {
      label: _t("kg_returned", "KG Returned"),
      value: summaryData?.data.kg_returned || "0",
      bgColor: "#fecdd3",
      textColor: "#f472b6",
    },
    {
      label: _t("kg_per_case_entered_sorting", "KG Per Case Entered Sorting"),
      value: summaryData?.data.kg_per_case_entered_sorting || "0",
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
    },
    {
      label: _t("kg_per_case_entered_processing", "KG Per Case Entered Processing"),
      value: summaryData?.data.kg_per_case_entered_processing || "0",
      bgColor: "#f0fdf4",
      textColor: "#4ade80",
    },
    {
      label: _t("percent_heavier_case_into_processing", "Percent Heavier Case Into Processing"),
      value: summaryData?.data.percent_heavier_case_into_processing || "0",
      bgColor: "#fef3c7",
      textColor: "#fb923c",
    },
    {
      label: _t("percent_heavier_28kg_baseline", "Percent Heavier at 28 KG baseline Entering Processing"),
      value: summaryData?.data.percent_heavier_28kg_baseline || "0",
      bgColor: "#fdf4ff",
      textColor: "#a78bfa",
    },
  ];

  // Filter labels and map
  const todayLabel = _t("today", "Today");
  const weeklyLabel = _t("weekly", "Weekly");
  const monthlyLabel = _t("monthly", "Monthly");
  const casesLabel = _t("cases", "Cases");
  const totalCasesLabel = _t("total_cases", "Total Cases");

  // Room Data Bar Chart Options
  const roomCapacity = _t("room_capacity", "Room Capacity");
  const totalCasesEntered = _t("total_cases_entered", "Total Cases Entered");
  const emptyRoomSpace = _t("empty_room_space", "Empty Space in Room");

  const optionsBar = {
    chart: { type: "column" },
    title: { text: null, align: "left" },
    xAxis: {
      categories: barChartData?.data?.categories,
      labels: {
        style: { fontSize: "12px", color: "#8C97A7" },
      },
    },
    yAxis: { title: { text: casesLabel } },
    legend: {
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
    },
    tooltip: { shared: true },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        name: roomCapacity,
        data: barChartData?.data?.data.map((room) => room.cases_capacity),
        color: "#ffcf86",
      },
      {
        name: totalCasesEntered,
        data: barChartData?.data?.data.map((room) => room.cases_entered),
        color: "#78db80",
      },
      {
        name: emptyRoomSpace,
        data: barChartData?.data?.data.map((room) => room.empty_space),
        color: "#ff8e8e",
      },
    ],
  };

  // Sorting Bar Chart Options
  const sortingCasesEnter = "Cases Enter in  Sorting";
  const sortingCasesSelected = "Cases Selected";
  const sortingCasesRejected = "Cases Rejected";

  const optionsSortingBar = {
    chart: { type: "column" },
    title: { text: null, align: "left" },
    xAxis: {
      categories: pieSortingData?.data?.data.map((item) => _t(item.name, item.name)),
      labels: {
        style: { fontSize: "12px", color: "#8C97A7" },
      },
    },
    yAxis: { title: { text: casesLabel } },
    legend: {
      enabled: false,
    },
    tooltip: { shared: true },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        name: casesLabel,
        data: pieSortingData?.data?.data.map((series) => ({
          y: series.y,
          color:
            series.name === sortingCasesEnter
              ? "#FFD699"
              : series.name === sortingCasesSelected
              ? "#FFB399"
              : series.name === sortingCasesRejected
              ? "#FFB3B3"
              : "#98D89D",
        })),
      },
    ],
  };

  const AddEmployee = () => {
    setShowAddEmployment(true);
  };

  return (
    <div>
      {showAddEmployment && (
        <AddRipeningEmployeeData
          show={showAddEmployment}
          setShow={setShowAddEmployment}
          record={employeeSummaryData?.data}
        />
      )}

      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="d-flex gap-2 align-items-center mb-4">
        <h5 className="m-0 flex-grow-1">{_t("ripening_overview", "Ripening Overview")}</h5>
        <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="ripening-date-range-filter" />
      </div>

      <div className="d-flex flex-column gap-4">
        {/* ── Cold Storage Overview Accordion ────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><SnowflakeIconSvg size={18} color="#60a5fa" />{_t("cold_storage_overview", "Cold Storage Overview")}</span>}>
          <div className="pp-brokering-grid">
            {coldStorageListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box" style={{ backgroundColor: item.bgColor }}>
                <div className="pp-brokering-box-label">{item.label}</div>
                <div className="pp-brokering-box-value">{formatValueWithUnit(item.label, item.value)}</div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Ripening Rooms Overview Accordion with Bar Chart ──────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><RipeningIconSvg size={18} color="#fb923c" />{_t("ripening_rooms_overview", "Ripening Rooms Overview")}</span>}>
          <div className="pp-brokering-grid mb-4">
            {ripeningRoomsListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box" style={{ backgroundColor: item.bgColor }}>
                <div className="pp-brokering-box-label">{item.label}</div>
                <div className="pp-brokering-box-value">{formatValueWithUnit(item.label, item.value)}</div>
              </div>
            ))}
          </div>
          {/* Room Data Bar Chart */}
          <div className="cmn_card">
            <div className="overview_header d-flex gap-2 align-items-center">
              <h6 className="mb-0 flex-grow-1">
                <span className="d-flex align-items-center gap-2"><BarChartIconSvg size={16} color="#60a5fa" />{_t("room_data_overview", "Room Data Overview")}</span>
              </h6>
            </div>
            <div className="p-2 position-relative">
              <HighchartsReact highcharts={Highcharts} options={optionsBar} />
            </div>
          </div>
        </DashboardSection>

        {/* ── Sorting Overview Accordion with Pie Chart ─────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><SortingIconSvg size={18} color="#60a5fa" />{_t("sorting_overview", "Sorting Overview")}</span>}>
          <div className="pp-brokering-grid mb-4">
            {sortingListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box" style={{ backgroundColor: item.bgColor }}>
                <div className="pp-brokering-box-label">{item.label}</div>
                <div className="pp-brokering-box-value">{formatValueWithUnit(item.label, item.value)}</div>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default RipeningDashboard;
